<script setup lang="ts">
  import { onBeforeUnmount, ref } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import {
    STARTER_CATEGORIES,
    TStarterCategory,
  } from '@/domain/catalog/starter-categories';

  /**
   * The empty store (plan §5.16): «Cargar desde fotos» (the view's one
   * orange; not for multi-menu stores), or start by hand with a starter
   * category, created on the tap.
   */
  const {
    layout,
    data,
    access,
    online,
    url,
    actions,
    commands,
  } = useCatalogPage();
  const creating = ref<TStarterCategory | null>(null);
  let unmounted = false;
  onBeforeUnmount(() => {
    unmounted = true;
  });

  async function createStarter(key: TStarterCategory, name: string) {
    if (creating.value) return;
    creating.value = key;
    try {
      await actions.createStarterCategory(name);
    } finally {
      // A created category replaces this state (it unmounts).
      if (!unmounted) creating.value = null;
    }
  }

  function otherCategory() {
    url.openCategory('new', layout.columns.value.asideDocked);
  }
</script>

<template>
  <div class="v2-catalog-empty-store">
    <PosUiEmptyState
      icon="silverware-fork-knife"
      :title="$t('menuPage.states.emptyStore')"
      :description="
        access.canManage && !data.multipleMenus.value
          ? $t('menuPage.states.emptyStoreHint')
          : undefined
      "
    >
      <template v-if="access.canManage && !data.multipleMenus.value" #action>
        <PosUiButton
          icon="camera-outline"
          @click="commands.runMoreCommand('import')"
          >{{ $t('menuPage.states.importPhotos') }}</PosUiButton
        >
      </template>
    </PosUiEmptyState>

    <div v-if="access.canManage" class="empty-manual">
      <p class="manual-title">{{ $t('menuPage.states.startByHand') }}</p>
      <div class="manual-chips">
        <button
          v-for="key in STARTER_CATEGORIES"
          :key="key"
          type="button"
          class="manual-chip"
          :disabled="!online || creating !== null"
          @click="createStarter(key, $t(`menuPage.starter.${key}`).toString())"
        >
          <i
            v-if="creating === key"
            class="mdi mdi-loading mdi-spin chip-busy"
          />
          {{ $t(`menuPage.starter.${key}`) }}
        </button>
        <button type="button" class="manual-chip" @click="otherCategory">
          {{ $t('menuPage.starter.other') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-empty-store {
    @apply tw-mx-auto tw-mt-6 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-surface tw-pb-8;
    max-width: 640px;

    .empty-manual {
      @apply tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100 tw-px-6 tw-pt-6 tw-text-center;
    }

    .manual-title {
      @apply tw-m-0 tw-text-sm tw-text-neutral-600;
    }

    // Margins, not flex gap (Chrome < 84).
    .manual-chips {
      @apply tw-mt-3 tw-flex tw-flex-wrap tw-justify-center;
    }

    .manual-chip {
      @apply tw-m-1 tw-inline-flex tw-h-11 tw-cursor-pointer tw-items-center tw-rounded-full tw-border-0 tw-bg-neutral-100 tw-px-4 tw-text-sm tw-font-medium tw-text-neutral-800 tw-transition-colors tw-duration-150;

      &:hover:not(:disabled) {
        @apply tw-bg-neutral-200;
      }

      &:disabled {
        @apply tw-cursor-default tw-opacity-60;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }

      .chip-busy {
        @apply tw-mr-1.5;
      }
    }
  }
</style>
